import {
	useQuery,
	keepPreviousData,
	useMutation,
	UseQueryOptions,
	QueryKey,
} from '@tanstack/react-query'
import {
	Address,
	Brand,
	Client,
	Document,
	Expense,
	GenericObject,
	Location,
	ObjectProperties,
	Payment,
	Proposal,
	ProposalItem,
	Reservation,
	ReservationWorkflowRequest,
	User,
	Vehicle,
} from '../../shared/types'
import { ButtonLink } from '../components/button'
import { del, get, post, put } from '../fetch'
import { MutationNotificationConfig } from '../query-client'
import { ParamsType } from '../scaffolding/types'
import { RequestBatcher } from './batch-loader'

export type ObjectParamsType = Record<string, unknown>

export type ObjectTypes = {
	address: Address
	brand: Brand
	client: Client
	document: Document
	expense: Expense
	image: GenericObject
	location: Location
	payment: Payment
	proposal: Proposal
	proposal_item: ProposalItem
	proposal_vehicle: GenericObject
	reservation: Reservation
	'reservation-workflow-request': ReservationWorkflowRequest
	user: User
	vehicle: Vehicle
}

const PRIMARY_OBJECTS = new Set<keyof ObjectTypes>([
	'address',
	'brand',
	'client',
	'document',
	'expense',
	'location',
	'payment',
	'proposal',
	'proposal_item',
	'proposal_vehicle',
	'reservation',
	'reservation-workflow-request',
	'user',
	'vehicle',
])

const getApi = (object: keyof ObjectTypes) => {
	if (PRIMARY_OBJECTS.has(object)) {
		return `/api/0/${object}`
	}
	return `/api/dito/0/${object}`
}

const SIDE_EFFECTS: Partial<Record<keyof ObjectTypes, Array<keyof ObjectTypes>>> = {
	payment: ['reservation'],
}

export function useObjectList<O extends keyof ObjectTypes>(
	object: O,
	searchParams: ParamsType & ObjectParamsType,
	options?: Pick<
		UseQueryOptions<{ total: number; list: ObjectTypes[O][] }>,
		| 'enabled'
		| 'refetchInterval'
		| 'refetchIntervalInBackground'
		| 'refetchOnWindowFocus'
		| 'staleTime'
	>,
) {
	return useQuery({
		placeholderData: keepPreviousData,
		staleTime: 30_000,
		...options,
		queryKey: ['api', object, searchParams],
		queryFn: () =>
			post<{ total: number; list: ObjectTypes[O][] }>(`${getApi(object)}`, searchParams),
	})
}

export function useObjectListAll<O extends keyof ObjectTypes, DT = ObjectTypes[O][]>(
	object: O,
	options?: Omit<UseQueryOptions<ObjectTypes[O][], Error, DT>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		...options,
		queryKey: ['api', object],
		queryFn: () => get<ObjectTypes[O][]>(`${getApi(object)}`),
		staleTime: 60_000,
	})
}

export type ObjectAggregateT = { total: number; rows: number; key: string }

export function useObjectAggregate<O extends keyof ObjectTypes>(
	object: O,
	parameters?: ObjectProperties,
) {
	return useQuery({
		queryKey: ['api', object, 'aggregate', parameters],
		queryFn: () => post<Array<ObjectAggregateT>>(`${getApi(object)}/aggregate`, parameters),
	})
}

export function useObjectGet<O extends keyof ObjectTypes>(object: O, id?: string | null) {
	return useQuery({
		queryKey: ['api', object, id],
		queryFn: () => get<ObjectTypes[O]>(`${getApi(object)}/${id}`),
		enabled: !!id,
		staleTime: 30_000,
	})
}

export function useObjectCreate<O extends keyof ObjectTypes>(object: O) {
	return useMutation({
		mutationFn: (data: FormData) => put<string | GenericObject>(`${getApi(object)}`, data),
		meta: {
			invalidateQueries: [
				['api', object],
				...(SIDE_EFFECTS[object] ?? []).map(reference => ['api', reference]),
			],
			notifications: {
				success: response => {
					const id =
						typeof response === 'string' ? response : (response as GenericObject).id
					return (
						<div className="flex items-center justify-between gap-4">
							Záznam vytvořen
							<ButtonLink
								to={`/svc/${object}/${id}`}
								size="SMALL"
								variant="SECONDARY"
							>
								Zobrazit
							</ButtonLink>
						</div>
					)
				},
			},
		},
	})
}

export function useObjectCreateMany<O extends keyof ObjectTypes>(object: O) {
	return useMutation({
		mutationFn: ({
			common,
			custom,
		}: {
			common: Partial<ObjectTypes[O]>
			custom: Array<Partial<ObjectTypes[O]>>
		}) => {
			return put<boolean>(`${getApi(object)}/many`, { common, custom })
		},
		meta: {
			invalidateQueries: [
				['api', object],
				...(SIDE_EFFECTS[object] ?? []).map(reference => ['api', reference]),
			],
			notifications: {
				success: () => 'Záznam vytvořeny',
			},
		},
	})
}

export function useObjectUpdate(object: keyof ObjectTypes, id: string) {
	return useMutation({
		mutationFn: (data: FormData | ObjectProperties) => post(`${getApi(object)}/${id}`, data),
		meta: {
			invalidateQueries: [['api', object]],
			notifications: {
				success: () => 'Změny uloženy',
			},
		},
	})
}

export function useObjectRemove(object: keyof ObjectTypes) {
	return useMutation({
		mutationFn: (id: string) => del(`${getApi(object)}/${id}`),
		meta: {
			invalidateQueries: [['api', object]],
			notifications: {
				success: () => 'Záznam smazán',
			},
		},
	})
}

export function useObjectActionQuery<T = unknown>(
	object: keyof ObjectTypes,
	id: string,
	action: string,
	options?: Omit<UseQueryOptions<T, Error>, 'queryKey' | 'queryFn'>,
) {
	return useQuery({
		queryKey: ['api', object, id, action],
		queryFn: () => get<T>(`${getApi(object)}/${id}/actions/${action}`),
		...options,
	})
}

export function useObjectActionMutation(
	object: keyof ObjectTypes,
	id: string,
	action: string,
	invalidateQueries: Array<QueryKey> = [],
	notifications?: Partial<MutationNotificationConfig>,
) {
	return useMutation({
		mutationFn: (data?: object) => post(`${getApi(object)}/${id}/actions/${action}`, data),
		meta: {
			invalidateQueries: [['api', object], ...invalidateQueries],
			notifications: {
				error: () => 'Operace selhala',
				...notifications,
			},
		},
	})
}

export function useObjectActionMutationVariable(
	object: keyof ObjectTypes,
	action: string,
	invalidateQueries: Array<QueryKey> = [],
	notifications?: Partial<MutationNotificationConfig>,
) {
	return useMutation({
		mutationFn: ({ id, ...data }: { id: string; [key: string]: unknown }) =>
			post(`${getApi(object)}/${id}/actions/${action}`, data),
		meta: {
			invalidateQueries: [['api', object], ...invalidateQueries],
			notifications: {
				error: () => 'Operace selhala',
				...notifications,
			},
		},
	})
}

function getObjectBatchFunction<O extends keyof ObjectTypes>(object: O) {
	return async function objectBatchFunction(ids: Array<string>) {
		const results = await post<Record<string, ObjectTypes[O]>>(`${getApi(object)}/batch`, {
			ids,
		})

		return ids.map(id => results[id])
	}
}

const batchers: Record<string, unknown> = {}

const getBatcher = <O extends keyof ObjectTypes>(object: O) => {
	batchers[object] ??= new RequestBatcher(getObjectBatchFunction(object))
	return batchers[object] as RequestBatcher<string, ObjectTypes[O]>
}

export function useObjectGetBatched<O extends keyof ObjectTypes>(object: O, id?: string | null) {
	return useQuery({
		queryKey: ['api', object, id],
		queryFn: ({ signal }) => getBatcher(object).load(id as string, signal),
		enabled: !!id,
		staleTime: 30_000,
	})
}
